<template>
  <div class="fixed inset-0 z-[100000] modal" :class="{ 'modal-open': visible !== false }">
    <div class="modal-box w-11/12 max-w-5xl overflow-y-auto max-h-[90vh] relative">
      <div class="sticky top-0 bg-white dark:bg-gray-800 z-10 pb-4">
        <h3 class="font-bold text-lg mb-4">{{ t('agentStyles.createTitle', 'Create New Style') }}</h3>
        <div role="tablist" class="tabs tabs-lifted">
          <input type="radio" name="style_tabs" role="tab" class="tab" :aria-label="t('agentStyles.tabs.configuration', 'Configuration')" checked />
          <div role="tabpanel" class="tab-content bg-base-100 border-base-300 rounded-box p-6">
            <div class="space-y-4">
              <div>
                <label class="label"><span class="label-text">{{ t('agentStyles.styleNameLabel', 'Style Name') }}</span></label>
                <input type="text" v-model="currentStyle.stylename" class="input input-bordered w-full" :placeholder="t('agentStyles.styleNamePlaceholder', 'Enter style name')" />
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 grid-space-4">
                <div>
                  <label class="label" id="primary-color-label"><span class="label-text">{{ t('agentStyles.primaryColorLabel', 'Primary Color') }}</span></label>
                  <input type="color" v-model="currentStyle.primaryColor" class="input input-bordered w-full h-10" />
                </div>
                <div>
                  <label class="label" id="secondary-color-label"><span class="label-text">{{ t('agentStyles.secondaryColorLabel', 'Secondary Color') }}</span></label>
                  <input type="color" v-model="currentStyle.secondaryColor" class="input input-bordered w-full h-10" />
                </div>
              </div>

              <div>
                <label class="label" id="chat-input-placeholder-label"><span class="label-text">{{ t('agentStyles.chatInputPlaceholderLabel', 'Chat Input Placeholder') }}</span></label>
                <input type="text" v-model="currentStyle.chatInputPlaceholder" class="input input-bordered w-full" :placeholder="t('agentStyles.chatInputPlaceholderPlaceholder', 'e.g., Type your question here...')" />
              </div>
              <div>
                <label class="label cursor-pointer" id="show-message-under-textbox-label">
                  <input type="checkbox" v-model="currentStyle.showMessageUnderTextbox" class="checkbox checkbox-primary" />
                  <span class="label-text ml-2">{{ t('agentStyles.showMessageUnderTextbox', 'Show message under chat input') }}</span>
                </label>
              </div>
              <div v-if="currentStyle.showMessageUnderTextbox === true">
                <label class="label" id="message-under-textbox-label"><span class="label-text">{{ t('agentStyles.placeholders.messageUnderTextbox', 'Enter message under chat input') }}</span></label>
                <input type="text" v-model="currentStyle.messageUnderTextbox" class="input input-bordered w-full" :placeholder="t('agentStyles.placeholders.messageUnderTextbox', 'Enter message under chat input')" />
              </div>
              <div>
                <label class="label cursor-pointer" id="show-terms-modal-label">
                  <input type="checkbox" v-model="currentStyle.showTermsModal" class="checkbox checkbox-primary" />
                  <span class="label-text ml-2">{{ t('agentStyles.showTermsModal', 'Show terms modal before chat') }}</span>
                </label>
              </div>
              <div v-if="currentStyle.showTermsModal === true">
                <label class="label" id="terms-modal-content-label"><span class="label-text">{{ t('agentStyles.placeholders.termsModalContent', 'Enter terms and conditions content') }}</span></label>
                <textarea v-model="currentStyle.termsModalContent" class="textarea textarea-bordered w-full h-40" :placeholder="t('agentStyles.placeholders.termsModalContent', 'Enter terms and conditions content')"></textarea>
              </div>
              <div v-if="currentStyle.showTermsModal === true">
                <label class="label" id="terms-button-text-label"><span class="label-text">{{ t('agentStyles.termsButtonTextLabel', 'Terms Button Text') }}</span></label>
                <input type="text" v-model="currentStyle.termsButtonText" class="input input-bordered w-full" :placeholder="t('agentStyles.placeholders.termsButtonText', 'Enter terms button text')" />
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 grid-space-6">
                <div>
                  <label class="label"><span class="label-text">{{ t('agentStyles.logoLabel', 'Logo') }}</span></label>
                  <div class="flex flex-col items-center">
                    <div class="w-full p-4 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 transition-colors duration-200" :class="{ 'border-primary': isDraggingLogo === true, 'border-gray-300': isDraggingLogo === false }" @dragenter.prevent="isDraggingLogo = true" @dragleave.prevent="isDraggingLogo = false" @dragover.prevent @drop.prevent="handleLogoDrop" @click="$refs.fileInput.click()">
                      <div class="flex flex-col items-center justify-center pt-3 pb-4">
                        <svg class="w-8 h-8 mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path></svg>
                        <p class="mb-1 text-xs text-gray-500 dark:text-gray-400"><span class="font-semibold">{{ t('agentStyles.clickOrDrag', 'Click or drag') }}</span> {{ t('agentStyles.logo', 'logo') }}</p>
                        <p class="text-xs text-gray-500 dark:text-gray-400">{{ t('agentStyles.logoHint', 'PNG, JPG, GIF (Max 5MB)') }}</p>
                      </div>
                      <input ref="fileInput" type="file" class="hidden" accept="image/png, image/jpeg, image/gif" @change="handleLogoUpload" />
                    </div>
                    <div v-if="currentStyle.logobase64 !== undefined && currentStyle.logobase64 !== null && currentStyle.logobase64 !== ''" class="mt-3 relative">
                      <img :src="currentStyle.logobase64 !== undefined && currentStyle.logobase64 !== null && currentStyle.logobase64 !== '' ? currentStyle.logobase64 : ''" alt="Logo preview" class="max-w-[200px] max-h-24 object-contain rounded-lg shadow-sm" />
                      <button @click.prevent="removeLogo" class="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors duration-200"><svg class="h-4 w-4" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" /></svg></button>
                    </div>
                  </div>
                </div>

                <div>
                  <label class="label"><span class="label-text">{{ t('agentStyles.chatIconLabel', 'Chat Icon') }}</span></label>
                  <div class="flex flex-col items-center">
                    <div class="w-full p-4 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 transition-colors duration-200" :class="{ 'border-primary': isDraggingIcon === true, 'border-gray-300': isDraggingIcon === false }" @dragenter.prevent="isDraggingIcon = true" @dragleave.prevent="isDraggingIcon = false" @dragover.prevent @drop.prevent="handleChatIconDrop" @click="$refs.chatIconInput.click()">
                      <div class="flex flex-col items-center justify-center pt-3 pb-4">
                        <svg class="w-8 h-8 mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path></svg>
                        <p class="mb-1 text-xs text-gray-500 dark:text-gray-400"><span class="font-semibold">{{ t('agentStyles.clickOrDrag', 'Click or drag') }}</span> {{ t('agentStyles.icon', 'icon') }}</p>
                        <p class="text-xs text-gray-500 dark:text-gray-400">{{ t('agentStyles.iconHint', 'SVG or PNG (Max 1MB)') }}</p>
                      </div>
                      <input ref="chatIconInput" id="chatIconInput" type="file" class="hidden" accept="image/svg+xml, image/png" @change="handleChatIconUpload" />
                    </div>
                    <div v-if="currentStyle.chatIconBase64 !== undefined && currentStyle.chatIconBase64 !== null && currentStyle.chatIconBase64 !== ''" class="mt-3 relative">
                      <img :src="currentStyle.chatIconBase64 !== undefined && currentStyle.chatIconBase64 !== null && currentStyle.chatIconBase64 !== '' ? currentStyle.chatIconBase64 : ''" alt="Chat Icon preview" class="max-w-[200px] max-h-20 object-contain rounded-lg shadow-sm" />
                      <button @click.prevent="removeChatIcon" class="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors duration-200"><svg class="h-4 w-4" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" /></svg></button>
                    </div>
                  </div>
                </div>
              </div>
              <div class="mb-6 p-4 border border-dashed rounded-lg" id="generate-from-screenshot-section">
                <h4 class="font-semibold text-lg mb-2">{{ t('agentStyles.generateFromScreenshot.title', 'Generate from Screenshot') }}</h4>
                <p class="text-sm text-gray-500 mb-4">{{ t('agentStyles.generateFromScreenshot.description', "Upload a screenshot of your website or your brand logo, and we'll automatically extract colors and suggest CSS for you.") }}</p>
                <input type="file" @change="handleAIDesignUpload" accept="image/png, image/jpeg, image/gif" class="file-input file-input-bordered w-full" :disabled="isGeneratingStyle === true" />
                <div v-if="isGeneratingStyle !== false" class="mt-4 flex items-center"><span class="loading loading-spinner loading-sm mr-2"></span><span>{{ t('agentStyles.generateFromScreenshot.loading', 'Analyzing image and generating styles...') }}</span></div>
              </div>
            </div>
          </div>
          <input type="radio" name="style_tabs" role="tab" class="tab" :aria-label="t('agentStyles.tabs.css', 'CSS')" />
          <div role="tabpanel" class="tab-content bg-base-100 border-base-300 rounded-box p-6">
            <div>
              <label class="label"><span class="label-text">{{ t('agentStyles.cssStylesLabel', 'CSS Styles') }}</span></label>
              <textarea v-model="currentStyle.cssstyles" class="textarea textarea-bordered w-full h-40"></textarea>
            </div>
          </div>
        </div>
      </div>
      <div class="sticky bottom-0 bg-white dark:bg-gray-800 pt-4 pb-2 modal-action z-10">
        <button class="btn" @click="close">{{ t('agentStyles.cancelButton', 'Cancel') }}</button>
        <button class="btn ml-4" @click="save">{{ t('agentStyles.saveButton', 'Save') }}</button>
      </div>
    </div>
    <form method="dialog" class="modal-backdrop"><button @click="close">close</button></form>
  </div>
</template>

<script setup>
import { ref, defineProps, defineEmits } from 'vue';
import axios from '@/api/axiosInstance';
import { useI18n } from '@/i18n';
import { globalState } from '@/globalState';

const props = defineProps({ visible: { type: Boolean, default: false } });
const emit = defineEmits(['close', 'created']);
const { t } = useI18n();

const currentStyle = ref({
  stylename: '',
  cssstyles: 'body {\n  \n}',
  primaryColor: '#000000',
  secondaryColor: '#ffffff',
  chatInputPlaceholder: '',
  showMessageUnderTextbox: false,
  messageUnderTextbox: '',
  showTermsModal: false,
  termsModalContent: '',
  termsButtonText: '',
  logobase64: '',
  chatIconBase64: ''
});


const isDraggingLogo = ref(false);
const isDraggingIcon = ref(false);
const isGeneratingStyle = ref(false);


function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });
}


async function handleLogoUpload(event) {
  isDraggingLogo.value = false;
  const file = event.target.files?.[0];
  if (file === undefined || file === null) return;
  currentStyle.value.logobase64 = await fileToBase64(file);
}
async function handleLogoDrop(event) {
  isDraggingLogo.value = false;
  const file = event.dataTransfer.files?.[0];
  if (file === undefined || file === null) return;
  currentStyle.value.logobase64 = await fileToBase64(file);
}
function removeLogo() {
  currentStyle.value.logobase64 = '';
}


async function handleChatIconUpload(event) {
  isDraggingIcon.value = false;
  const file = event.target.files?.[0];
  if (file === undefined || file === null) return;
  currentStyle.value.chatIconBase64 = await fileToBase64(file);
}
async function handleChatIconDrop(event) {
  isDraggingIcon.value = false;
  const file = event.dataTransfer.files?.[0];
  if (file === undefined || file === null) return;
  currentStyle.value.chatIconBase64 = await fileToBase64(file);
}
function removeChatIcon() {
  currentStyle.value.chatIconBase64 = '';
}


async function handleAIDesignUpload(event) {
  const file = event.target.files?.[0];
  if (file === undefined || file === null) return;
  isGeneratingStyle.value = true;
  try {
    const base64 = await fileToBase64(file);
    const response = await axios.post(`${globalState.apiEndpoint}/api/styles/generateFromImage`, { imageBase64: base64 });
    currentStyle.value.primaryColor = response.data.primaryColor;
    currentStyle.value.secondaryColor = response.data.secondaryColor;
    currentStyle.value.cssstyles = response.data.cssStyles;
  } catch (error) {
    console.error('Error generating style:', error);
  } finally {
    isGeneratingStyle.value = false;
  }
}

async function save() {
  if (currentStyle.value.stylename.trim().length === 0) {
    return;
  }
  try {
    const response = await axios.post(`${globalState.apiEndpoint}/api/styles/createNewAgentStyle`, {
      styleName: currentStyle.value.stylename.trim(),
      cssStyles: currentStyle.value.cssstyles.trim(),
      logoBase64: currentStyle.value.logobase64,
      primaryColor: currentStyle.value.primaryColor,
      secondaryColor: currentStyle.value.secondaryColor,
      chatIconBase64: currentStyle.value.chatIconBase64,
      chatInputPlaceholder: currentStyle.value.chatInputPlaceholder,
      showMessageUnderTextbox: currentStyle.value.showMessageUnderTextbox,
      messageUnderTextbox: currentStyle.value.messageUnderTextbox,
      showTermsModal: currentStyle.value.showTermsModal,
      termsModalContent: currentStyle.value.termsModalContent,
      termsButtonText: currentStyle.value.termsButtonText
    });
    emit('created', response.data);
  } catch (error) {
    console.error('Error creating style:', error);
    return;
  }
  close();
}

function close() {
  emit('close');
  currentStyle.value = {
    stylename: '',
    cssstyles: 'body {\n  \n}',
    primaryColor: '#000000',
    secondaryColor: '#ffffff',
    chatInputPlaceholder: '',
    showMessageUnderTextbox: false,
    messageUnderTextbox: '',
    showTermsModal: false,
    termsModalContent: '',
    termsButtonText: '',
    logobase64: '',
    chatIconBase64: ''
  };
}
</script>
<style scoped>
.modal-open .modal { display: flex; }
</style>